/**
 * Frame-accurate, mostly-sequential video frame access for export, built on mediabunny.
 * One `Input` per media file; one `SequentialFrameReader` per layer instance, so the same media used
 * twice at different times decodes independently.
 */
import {
  ALL_FORMATS,
  CanvasSink,
  Input,
  VideoSample,
  VideoSampleSink,
  type InputVideoTrack,
  type WrappedCanvas,
} from 'mediabunny';
import { transferOf, type TransferFunction } from '../media/color.ts';
import { describeInput, inputKey, sourceFor } from '../media/input.ts';
import { shouldResetReader } from '../media/sync.ts';
import type { MediaInput } from '../types.ts';

export class MediaInputs {
  private inputs = new Map<string, { input: Input; track: Promise<InputVideoTrack> }>();

  videoTrack(media: MediaInput): Promise<InputVideoTrack> {
    const key = inputKey(media);
    let entry = this.inputs.get(key);
    if (!entry) {
      const input = new Input({ source: sourceFor(media), formats: ALL_FORMATS });
      const track = (async () => {
        const t = await input.getPrimaryVideoTrack();
        if (!t) throw new Error(`No video track in ${describeInput(media)}`);
        if (!(await t.canDecode())) {
          throw new Error(
            `This browser cannot decode the video codec (${t.codec ?? 'unknown'}) of ${describeInput(media)}`,
          );
        }
        return t;
      })();
      entry = { input, track };
      this.inputs.set(key, entry);
    }
    return entry.track;
  }

  dispose(): void {
    for (const { input } of this.inputs.values()) input.dispose();
    this.inputs.clear();
  }
}

/** A decoded frame: a canvas (SDR, rotation applied) or a raw sample (HDR, see `create`). */
export type ReaderFrame = WrappedCanvas | VideoSample;

interface FrameSink<T extends ReaderFrame> {
  frames(start: number): AsyncGenerator<T, void, unknown>;
  frameAt(time: number): Promise<T | null>;
}

export class SequentialFrameReader<T extends ReaderFrame = ReaderFrame> {
  private iterator: AsyncGenerator<T, void, unknown> | null = null;
  private current: T | null = null;
  private next: T | null = null;
  private exhausted = false;
  lastUsed = 0;

  constructor(
    private readonly sink: FrameSink<T>,
    readonly transfer: TransferFunction = 'sdr',
  ) {}

  /**
   * SDR video is read through a `CanvasSink` (rotation applied, a small canvas pool keeps VRAM
   * constant). HDR video is read as raw samples instead: drawing it into a 2D canvas would let the
   * browser tone-map it, whereas the compositor needs the original signal to do that itself.
   */
  static async create(track: InputVideoTrack): Promise<SequentialFrameReader> {
    const transfer = transferOf(await track.getColorSpace());
    if (transfer !== 'sdr') {
      const samples = new VideoSampleSink(track);
      return new SequentialFrameReader<VideoSample>(
        { frames: (start) => samples.samples(start), frameAt: (time) => samples.getSample(time) },
        transfer,
      );
    }
    // We hold at most `current` + `next` of the pool.
    const canvases = new CanvasSink(track, { poolSize: 4 });
    return new SequentialFrameReader<WrappedCanvas>({
      frames: (start) => canvases.canvases(start),
      frameAt: (time) => canvases.getCanvas(time),
    });
  }

  /** Returns the frame displayed at `time` (seconds), or the last frame if past the end. */
  async frameAt(time: number): Promise<T | null> {
    if (shouldResetReader(this.current?.timestamp ?? null, time)) await this.reset(time);
    for (;;) {
      if (!this.next && !this.exhausted) {
        const r = await this.iterator!.next();
        if (r.done) this.exhausted = true;
        else this.next = r.value;
      }
      if (this.next && this.next.timestamp <= time + 1e-6) {
        release(this.current);
        this.current = this.next;
        this.next = null;
      } else {
        break;
      }
    }
    return this.current;
  }

  async close(): Promise<void> {
    await this.iterator?.return();
    this.iterator = null;
    release(this.current);
    release(this.next);
    this.current = null;
    this.next = null;
  }

  private async reset(time: number): Promise<void> {
    await this.iterator?.return();
    release(this.current);
    release(this.next);
    this.iterator = this.sink.frames(Math.max(0, time));
    this.exhausted = false;
    this.next = null;
    const first = await this.iterator.next();
    if (first.done) {
      // Past the last frame: fall back to the frame displayed at `time` (the last one).
      this.exhausted = true;
      this.current = await this.sink.frameAt(time);
    } else {
      this.current = first.value;
    }
  }
}

/** Samples own decoder memory and must be closed; pooled canvases are simply reused. */
function release(frame: ReaderFrame | null): void {
  if (frame instanceof VideoSample) frame.close();
}
